// Adapt UTM value sources to the campaign spreadsheet without dictionary code
// substitution. Complete taxonomy names and dictionary values stay intact.
export function utmSpreadsheetConfig(fields, options) {
  return { fields: fields.map(field => ['dropdown', 'generated_taxonomy'].includes(field.field_type)
    ? { ...field, field_type: 'dropdown', dict_list_id: null, validation_regex: null,
        dictionary: (options[field.id] || []).map(value => ({ id: value, value })) }
    : field.field_type === 'date' ? { ...field, validation_regex: null } : field) };
}

export const utmSpreadsheetOutput = row => row.url || '';
export const utmSpreadsheetError = (row, field, diagnostics = {}) => row.errors?.[field.id] || row.review?.[field.id]?.reason
  || ((field.is_required || row.values?.[field.id]) ? diagnostics[field.id] : null) || null;
export const utmSpreadsheetComplete = row => row.status === 'valid';

// A separate UTM edit session; taxonomy persistence is not changed.
export function createUtmSession(api, initial) {
  let state = { ...initial, rows: initial.rows || [], status: '', error: '', dirty: false };
  let generation = 0, refreshId = 0, timer, running;
  const listeners = new Set();
  const emit = patch => { state = { ...state, ...patch }; listeners.forEach(fn => fn()); };
  const session = {
    getSnapshot: () => state,
    subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn); },
    setData: data => { generation++; emit({ ...data, dirty: false, error: '', status: '' }); },
    refresh: async (load, { discard = false, isCurrent = () => true } = {}) => {
      const request = ++refreshId;
      if (discard) {
        clearTimeout(timer);
        while (running) await running.catch(() => {});
      } else if (state.dirty) return false;
      const started = generation;
      const data = await load();
      // Even an edit that finished saving while this read was pending makes it stale.
      if (request !== refreshId || started !== generation || !isCurrent()) return false;
      session.setData(data);
      return true;
    },
    change: rows => {
      generation++; emit({ rows, dirty: true, status: 'Unsaved changes', error: '' });
      clearTimeout(timer); timer = setTimeout(() => session.flush().catch(() => {}), 1500);
    },
    flush: async () => {
      clearTimeout(timer);
      if (running) { await running; if (state.dirty) return session.flush(); return; }
      if (!state.dirty) return;
      const sent = generation;
      emit({ status: 'Saving…', error: '' });
      running = api(state).then(data => {
        if (sent === generation) emit({ ...data, dirty: false, status: 'All changes saved' });
        else emit({ revision: data.revision, status: 'Unsaved changes' });
      }).catch(e => { emit({ error: e.message, status: 'Save failed' }); throw e; }).finally(() => { running = null; });
      await running;
      if (state.dirty) return session.flush();
    },
  };
  return session;
}

export function pasteCells(rows, fields, startId, startField, matrix, uuid) {
  const out = rows.map(r => ({ ...r, values: { ...r.values } }));
  let start = out.findIndex(r => r.id === startId);
  if (start < 0) start = out.length;
  matrix.forEach((cells, offset) => {
    const index = start + offset;
    if (!out[index]) out[index] = { id: uuid(), values: {} };
    cells.forEach((value, col) => {
      const field = fields[startField + col];
      if (field) out[index].values[field.id] = String(value ?? '');
    });
  });
  return out;
}

export function tsv(rows) {
  return rows.map(row => row.map(v => {
    const s = String(v ?? '');
    return /[\t\n\r"]/.test(s) ? '"' + s.replaceAll('"', '""') + '"' : s;
  }).join('\t')).join('\n');
}
